import { combineEpics, ofType } from 'redux-observable';
import { EMPTY, forkJoin, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { replaceLocation } from '../actions/common';
import {
    GET_USER_SETTINGS_DATA,
    LOGIN_TO_MEDIASCOPE,
    LOGOUT_FROM_MEDIASCOPE,
    updateUserSettingsData
} from '../actions/userSettings';
import { awaitCommonData } from '../operators';
import { getOidcUserManager } from '../service/Oidc';
import { UrlHelpers } from '../service/UrlHelpers';
import { MeasurerSystem } from '../typings/common';
import { DnaEpic, DnaEpicAction } from '../typings/epic';

const getUserSettingsDataEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(GET_USER_SETTINGS_DATA),
        awaitCommonData(action$, store$),
        switchMap(
            (): DnaEpicAction => {
                const state = store$.value;

                const {
                    router: { query }
                } = state;

                const login = state.commonData.login;

                const processEpic = () =>
                    from(deps.userSettingsDataProvider.fetchMeasurerSystem(state.commonData.login)).pipe(
                        switchMap(
                            (measurerOptions): DnaEpicAction => {
                                return [
                                    replaceLocation(UrlHelpers.getUserSettingsUrl(login), undefined, false),
                                    updateUserSettingsData(measurerOptions)
                                ];
                            }
                        )
                    );

                if (query && query.state && query.session_state && query.code) {
                    return from(getOidcUserManager(login)).pipe(
                        switchMap(
                            (OidUserManager): DnaEpicAction => {
                                return from(OidUserManager.signinRedirectCallback()).pipe(
                                    switchMap(
                                        ({ access_token, expires_at, refresh_token }): DnaEpicAction => {
                                            const settings = { access_token, expires_at, refresh_token };

                                            return from(
                                                deps.userSettingsDataProvider.addMeasurerAccount(
                                                    login,
                                                    settings,
                                                    MeasurerSystem.MEDIASCOPE
                                                )
                                            ).pipe(switchMap((): DnaEpicAction => processEpic()));
                                        }
                                    ),
                                    catchError(() => {
                                        return processEpic();
                                    })
                                );
                            }
                        )
                    );
                }

                return processEpic();
            }
        )
    );
};

const logoutFromMediascopeEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(LOGOUT_FROM_MEDIASCOPE),
        switchMap(
            (): DnaEpicAction => {
                const state = store$.value;

                const login = state.commonData.login;

                return forkJoin([
                    getOidcUserManager(login),
                    deps.userSettingsDataProvider.deleteMeasurerAccount(login, MeasurerSystem.MEDIASCOPE)
                ]).pipe(
                    switchMap(
                        ([OidUserManager]): DnaEpicAction => {
                            OidUserManager.signoutRedirect();
                            return EMPTY;
                        }
                    )
                );
            }
        )
    );
};

const loginToMediascopeEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(LOGIN_TO_MEDIASCOPE),
        switchMap(
            (): DnaEpicAction => {
                const state = store$.value;
                const login = state.commonData.login;

                return from(getOidcUserManager(login)).pipe(
                    switchMap(OidcUserManager => {
                        OidcUserManager.signinRedirect();
                        return EMPTY;
                    })
                );
            }
        )
    );
};

export const userSettingsEpic = combineEpics(getUserSettingsDataEpic, logoutFromMediascopeEpic, loginToMediascopeEpic);
